import React, { useEffect } from 'react';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { useCoreDataContext } from 'src/app/contexts/useCoreDataContext';
import { useAppDispatch, useAppSelector } from 'src/app/redux/createAction';
import { preventSubmitOnEnter } from 'src/helpers/formHelpers';
import { Company, DashBoardMenuName, MetaTab, PropertiesCompany, PropertyMeta, SingleMenuItem } from 'src/pages/CompanyPage/_BLL/types';
import { FieldGeneratorCompany } from 'src/pages/CompanyPage/ui//FieldGeneratorCompany/FieldGeneratorCompany';
import { RequestStatus } from 'src/shared/api/types';
import { ButtonSecondary } from 'src/shared/ui/_buttons/ButtonSecondary';
import { Heading } from 'src/shared/ui/Heading';
import { actionsCompany } from '../../_BLL/slice';
import { useCompanyFormContext } from '../../lib/context/useCompanyPageContext';
import s from './FormGroupCompany.module.scss';
import { FinalizingMessage } from '../FinalizingMessage/FinalizingMessage';

const findMenuOrder = (activeMenuName: DashBoardMenuName, menuNames?: { name: DashBoardMenuName; index: number }[]) => {
	const menuIndex = menuNames?.find((item) => item.name === activeMenuName)?.index;
	return menuIndex !== undefined ? menuIndex : 111; // ? Should not happen, but 111 Means fail.
};

interface Props {
	companyActiveMenuMeta: MetaTab;
	getFormState: (formState: FormState<Company>) => void;
	getFormValues: (values: { properties: PropertiesCompany; id: number; entityName: 'company'; displayName: string }) => void;
}

export const FormGroupCompany: React.FC<Props> = (props) => {
	const {
		companyActiveMenuMeta, //
		getFormState,
		getFormValues,
	} = props;

	// * Context
	const { dictionaries } = useCoreDataContext();
	const { currentCompany } = useCompanyFormContext();

	// * Selectors
	const menuItems = useAppSelector((state) => state.company.menuItems);
	const activeMenuName = useAppSelector((state) => state.company.activeMenu);
	const status = useAppSelector((state) => state.company.status);

	// * Actions
	const dispatch = useAppDispatch();
	const { updateCompany, setActiveMenuOption } = actionsCompany;

	// * Company meta
	const allPropertyNames = companyActiveMenuMeta?.groups.map((group) => group.properties.map((property) => property.propertyName)).flat();
	const properties = allPropertyNames?.reduce((acc, propertyName) => ({ ...acc, [propertyName]: currentCompany.properties[propertyName] }), {}) as Company['properties'];

	const defaultValues = {
		...currentCompany,
		properties,
	};

	// * Company Form
	const companyFormMethods = useForm({
		defaultValues,
	});

	// * Menu
	const handleSetActiveMenuOption = (menuItem: DashBoardMenuName) => dispatch(setActiveMenuOption(menuItem));

	const menuNamesWithIndex = menuItems.map((item: SingleMenuItem, index: number) => ({
		name: item.name,
		index,
	}));

	const menuOrder = findMenuOrder(activeMenuName, menuNamesWithIndex);

	const { handleSubmit, formState, trigger, getValues } = companyFormMethods;

	useEffect(() => {
		getFormState(formState);
	}, [formState]);

	useEffect(() => {
		getFormValues(getValues());
	}, []);

	const onSubmit = (values: Company, nextMenuName: DashBoardMenuName | undefined, showNotification: boolean = false) => {
		dispatch(
			updateCompany({
				payload: values,
				submitForRating: showNotification,
			}),
		)
			.unwrap()
			.then(() => {
				nextMenuName && handleSetActiveMenuOption(nextMenuName);
			})
			.catch((error) => console.log('error :>> ', error));
	};

	const submitForm = (nextMenuName: DashBoardMenuName | undefined, showNotification?: boolean) => handleSubmit((values) => onSubmit(values, nextMenuName, showNotification))();

	const errorsCount = formState.errors?.properties ? Object.keys(formState.errors.properties).length : 0;

	const nextMenu = (next: 'forward' | 'back') => {
		const activeMenuIndex = menuNamesWithIndex?.find((menu) => menu.name === activeMenuName)?.index;
		const step = next === 'forward' ? activeMenuIndex !== undefined && activeMenuIndex + 1 : activeMenuIndex !== undefined && activeMenuIndex - 1;
		const nextMenuName = menuNamesWithIndex?.find((menu) => menu.index === step)?.name;
		if (activeMenuIndex === 0) {
			nextMenuName && handleSetActiveMenuOption(nextMenuName);
		} else {
			submitForm(nextMenuName);
		}
	};

	const forward = () => {
		trigger().then((noErrors) => {
			if (noErrors) {
				nextMenu('forward');
			}
		});
	};

	const backward = () => {
		trigger().then((noErrors) => {
			if (noErrors) {
				nextMenu('back');
			}
		});
	};

	// * Render
	return (
		<FormProvider {...companyFormMethods}>
			<form
				className={s.container}
				onKeyDown={(e) => preventSubmitOnEnter(e)}
			>
				<div className={s.inner}>
					{companyActiveMenuMeta.groups.map((group, groupIndex) => (
						<React.Fragment key={groupIndex}>
							<Heading
								level={2}
								size={3}
								marginBottom="m"
							>
								{group.name}
							</Heading>

							{dictionaries &&
								group.properties
									.filter((property: PropertyMeta) => property.propertyName !== 'id') // ! HACK: Hide id field
									.map((property: PropertyMeta, index) => (
										<FieldGeneratorCompany
											key={property.propertyName}
											orderPrefix={`${menuOrder}.${groupIndex + 1}.${index + 1}`}
											meta={property}
											dictionaries={dictionaries}
											namePrefix="properties."
										/>
									))}
						</React.Fragment>
					))}
				</div>

				<div className={s.buttonsWrapper}>
					<ButtonSecondary
						onClick={backward}
						loading={status === RequestStatus.loading}
					>
						Previous
					</ButtonSecondary>

					<ButtonSecondary
						onClick={forward}
						loading={status === RequestStatus.loading}
					>
						Next
					</ButtonSecondary>

					{errorsCount > 0 && (
						<span className={s.error}>
							Please fill {errorsCount} field{errorsCount > 1 ? 's' : ''} before continue.
						</span>
					)}

					<FinalizingMessage />
				</div>
			</form>
		</FormProvider>
	);
};
